import { useMemo, useState } from 'react';
import { Achievements } from '../components/Achievements';
import { Button } from '../components/Button';
import { DailyCalendar } from '../components/DailyCalendar';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { SegmentedControl } from '../components/SegmentedControl';
import { PlantProgress } from '../components/PlantProgress';
import { allModes } from '../engine/registry';
import { dateForDay } from '../engine/seed';
import type { AnyPuzzleMode } from '../engine/types';
import { useToday } from '../hooks/useToday';
import { gardenFor } from '../state/garden';
import { useGame } from '../state/GameContext';
import { useNav } from '../state/NavContext';
import { useSiteConfig } from '../state/SiteConfigContext';
import { liveStreak, overall } from '../state/statsLogic';
import { useStats } from '../state/StatsContext';
import { accentVars } from '../utils/accent';
import { commas, pct } from '../utils/format';
import { ResultModal, viewFromDaily } from './ResultModal';

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="cv-stat">
      <span className="cv-stat__value">{value}</span>
      <span className="cv-stat__label">{label}</span>
    </div>
  );
}

function DistributionChart({ dist, max }: { dist: Record<number, number>; max: number }) {
  const counts = Array.from({ length: max }, (_, i) => dist[i + 1] ?? 0);
  const peak = Math.max(1, ...counts);
  return (
    <div className="cv-dist">
      <span className="cv-eyebrow">Guess distribution · hint-free wins</span>
      {counts.map((count, i) => (
        <div className="cv-meter" key={i}>
          <span className="cv-mono">{i + 1}</span>
          <div
            className="cv-meter__bar"
            data-highlight={count === peak && count > 0 ? 'true' : undefined}
            style={{ width: `${Math.max(8, (count / peak) * 100)}%` }}
          >
            {count}
          </div>
        </div>
      ))}
    </div>
  );
}

/** One day's dailies: review what you played, or play the rest from the archive. */
function DaySheet({
  day,
  today,
  modes,
  onClose,
}: {
  day: number;
  today: number;
  modes: AnyPuzzleMode[];
  onClose: () => void;
}) {
  const { stats } = useStats();
  const game = useGame();
  const { navigate } = useNav();
  const parkedKeys = useMemo(() => new Set(game.parked.map((p) => p.key)), [game.parked]);
  const garden = gardenFor(stats, day, modes, parkedKeys);
  const [viewing, setViewing] = useState<AnyPuzzleMode | null>(null);
  const isToday = day === today;
  const title = dateForDay(day).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const play = (mode: AnyPuzzleMode) => {
    const outcome = game.start({ modeId: mode.id, playMode: 'daily', day });
    if (outcome === 'started' || outcome === 'resumed') navigate('play');
    else if (outcome === 'done') setViewing(mode);
  };

  const viewRec = viewing ? stats.dailies[String(day)]?.[viewing.id] : undefined;

  if (viewing && viewRec?.result) {
    return (
      <ResultModal
        view={viewFromDaily(viewing, day, Boolean(viewRec.archive), viewRec.result)}
        onClose={() => setViewing(null)}
        onHome={() => setViewing(null)}
      />
    );
  }

  return (
    <Modal open onClose={onClose} title={`Daily #${day}`}>
      <p className="cv-muted" style={{ marginBottom: 'var(--space-sm)' }}>
        {title} · {garden.won}/{garden.total} solved
        {!isToday ? ' · archive plays don’t affect streaks' : ''}
      </p>
      <ul className="cv-dayplan">
        {garden.entries.map((e) => (
          <li key={e.mode.id} className="cv-dayplan__row" style={accentVars(e.mode.accent)}>
            <span className="cv-mode-card__glyph" style={{ width: '2.2rem', height: '2.2rem' }}>
              <Icon name={e.mode.glyph} size={16} />
            </span>
            <span className="cv-dayplan__name">
              <strong>{e.mode.name}</strong>
              <span className="cv-faint">
                {e.record
                  ? `${e.record.status === 'won' ? 'Solved' : 'Missed'} · ${commas(e.record.score)}${e.record.archive ? ' · archive' : ''}`
                  : e.growing
                    ? 'In progress'
                    : 'Not played'}
              </span>
            </span>
            {e.record ? (
              e.record.result ? (
                <Button size="sm" variant="ghost" onClick={() => setViewing(e.mode)}>
                  View
                </Button>
              ) : (
                <Icon name={e.record.status === 'won' ? 'check' : 'close'} size={18} />
              )
            ) : (
              <Button size="sm" variant="secondary" iconLeft="play" onClick={() => play(e.mode)}>
                {e.growing ? 'Continue' : 'Play'}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Modal>
  );
}

export function StatsScreen() {
  const { stats, modeStats } = useStats();
  const { isModeEnabled } = useSiteConfig();
  const today = useToday();
  const modes = allModes();
  const enabled = modes.filter((m) => isModeEnabled(m.id));
  const [tab, setTab] = useState<string>('all');
  const [pickedDay, setPickedDay] = useState<number | null>(null);
  const o = overall(stats, today);

  const options = [{ value: 'all', label: 'All' }, ...modes.map((m) => ({ value: m.id, label: m.name }))];

  return (
    <div className="cv-stats">
      <div className="cv-page-head">
        <span className="cv-eyebrow">Performance</span>
        <h1>Your stats</h1>
      </div>

      <div className="cv-stats__scope">
        <SegmentedControl<string> ariaLabel="Stats scope" value={tab} onChange={setTab} options={options} />
      </div>

      {tab === 'all' ? (
        <>
          <div className="cv-stats__grid">
            <Stat value={o.played} label="Played" />
            <Stat value={o.won} label="Won" />
            <Stat value={o.played ? pct(o.won / o.played) : '—'} label="Win rate" />
            <Stat value={o.garden.current} label="Garden streak" />
            <Stat value={o.bestStreak} label="Best streak" />
            <Stat value={commas(o.totalScore)} label="Total score" />
          </div>
          <div className="cv-stats__garden">
            <PlantProgress streak={o.currentBestStreak} />
            <div className="cv-settings__group cv-stats__calendar">
              <span className="cv-eyebrow">Daily calendar</span>
              <DailyCalendar dailies={stats.dailies} modeCount={enabled.length} onPickDay={setPickedDay} />
              <p className="cv-help">Tap a day to review it or play the dailies you missed.</p>
            </div>
          </div>

          <div className="cv-settings__group">
            <span className="cv-eyebrow">Achievements</span>
            <Achievements stats={stats} />
          </div>
        </>
      ) : (
        (() => {
          const mode = modes.find((m) => m.id === tab);
          const s = modeStats(tab);
          return (
            <>
              <div className="cv-stats__grid">
                <Stat value={s.played} label="Played" />
                <Stat value={s.played ? pct(s.won / s.played) : '—'} label="Win rate" />
                <Stat value={liveStreak(s, today)} label="Streak" />
                <Stat value={s.maxStreak} label="Best streak" />
                <Stat value={commas(s.bestScore)} label="Best score" />
                <Stat value={s.played ? commas(s.totalScore / s.played) : '—'} label="Avg score" />
              </div>
              {tab === 'cipher' && s.won > 0 ? <DistributionChart dist={s.guessDistribution} max={6} /> : null}
              <PlantProgress streak={liveStreak(s, today)} scope={mode?.name} />
              {mode ? <p className="cv-faint cv-center">{mode.tagline}</p> : null}
            </>
          );
        })()
      )}

      {pickedDay != null ? (
        <DaySheet day={pickedDay} today={today} modes={enabled} onClose={() => setPickedDay(null)} />
      ) : null}
    </div>
  );
}
